import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../../../lib/utils";

export type BadgeVariant = "filled" | "outlined" | "transparent";
export type BadgeState = "primary" | "error" | "warning" | "cta" | "secondary";

const badgeVariants = cva(
  "inline-flex items-center justify-center border font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        filled: "border-0",
        outlined: "bg-transparent",
        transparent: "border-transparent bg-transparent",
      },
      state: {
        primary:
          "bg-semantic-primary-background-normal-default text-semantic-static-global-global-white border-semantic-primary-background-normal-default",
        error:
          "bg-semantic-error-global-global-default text-semantic-static-global-global-white border-semantic-error-global-global-default",
        warning:
          "bg-semantic-warning-global-global-defualt text-semantic-static-global-global-white border-semantic-warning-global-global-defualt",
        cta: "bg-semantic-cta-background-strong-default text-semantic-static-global-global-white border-semantic-cta-background-strong-default",
        secondary:
          "bg-semantic-secondary-background-heavy-defualt text-semantic-secondary-foreground-heavy-default border-semantic-secondary-background-heavy-defualt",
      },
      size: {
        md: "min-w-[16px] px-sp40 py-sp20 typography-semantic-label-2-regular",
        lg: "min-w-[24px] px-sp40 py-sp20 typography-semantic-label-1-normal---regular",
      },
      rounded: {
        circle: "rounded-full",
        square: "rounded-sm",
      },
    },
    compoundVariants: [
      {
        variant: "outlined",
        state: "primary",
        className:
          "text-semantic-primary-foreground-global-default bg-transparent",
      },
      {
        variant: "outlined",
        state: "error",
        className: "text-semantic-error-global-global-default bg-transparent",
      },
      {
        variant: "outlined",
        state: "warning",
        className: "text-semantic-warning-global-global-defualt bg-transparent",
      },
      {
        variant: "outlined",
        state: "cta",
        className: "text-semantic-cta-background-strong-default bg-transparent",
      },
      {
        variant: "outlined",
        state: "secondary",
        className:
          "text-semantic-secondary-foreground-heavy-default bg-transparent",
      },
      {
        variant: "transparent",
        state: "primary",
        className:
          "text-semantic-primary-foreground-global-default border-none bg-transparent",
      },
      {
        variant: "transparent",
        state: "error",
        className:
          "text-semantic-error-global-global-default border-none bg-transparent",
      },
      {
        variant: "transparent",
        state: "warning",
        className:
          "text-semantic-warning-global-global-defualt border-none bg-transparent",
      },
      {
        variant: "transparent",
        state: "cta",
        className:
          "text-semantic-cta-background-strong-default border-none bg-transparent",
      },
      {
        variant: "transparent",
        state: "secondary",
        className: "text-secondary bg-transparent",
      },
    ],
    defaultVariants: {
      variant: "filled",
      state: "primary",
      size: "md",
      rounded: "circle",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({
  className,
  variant,
  state,
  size,
  rounded,
  ...props
}: BadgeProps) {
  return (
    <div
      className={cn(
        badgeVariants({ variant, state, size, rounded }),
        className
      )}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
